import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { message } from 'antd';
import { useStateContext } from '../../../../../contexts/ContextProvider';

export const useCategories = () => {
  const { token } = useStateContext();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categoriesTree, setCategoriesTree] = useState([]);

  const fetchCategories = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/product_category`, {
        params: { token }
      });
      
      const categoriesData = response.data || [];
      setCategories(categoriesData);
      
      // Construim arborele de categorii
      const tree = buildCategoryTree(categoriesData);
      setCategoriesTree(tree);
    } catch (error) {
      console.error('Eroare la încărcarea categoriilor:', error);
      message.error('Eroare la încărcarea categoriilor');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const buildCategoryTree = (categories) => {
    const categoryMap = {};
    const tree = [];

    // Creăm o mapă pentru acces rapid
    categories.forEach(category => {
      categoryMap[category.id] = { ...category, children: [] };
    });

    // Construim arborele
    categories.forEach(category => {
      if (category.parent_id && categoryMap[category.parent_id]) {
        categoryMap[category.parent_id].children.push(categoryMap[category.id]);
      } else {
        tree.push(categoryMap[category.id]);
      }
    });

    return tree;
  };

  const createCategory = useCallback(async (categoryData) => {
    if (!token) return false;
    
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/product_category`, categoryData, {
        params: { token }
      });
      
      message.success('Categoria a fost creată cu succes');
      fetchCategories();
      return response.data;
    } catch (error) {
      console.error('Eroare la crearea categoriei:', error);
      message.error(error.response?.data?.detail || 'Eroare la crearea categoriei');
      return false;
    }
  }, [token, fetchCategories]);

  const updateCategory = useCallback(async (id, categoryData) => {
    if (!token) return false;
    
    try {
      const response = await axios.put(`${process.env.REACT_APP_API_URL}/product_category/${id}`, categoryData, {
        params: { token }
      });
      
      message.success('Categoria a fost actualizată cu succes');
      fetchCategories();
      return response.data;
    } catch (error) {
      console.error('Eroare la actualizarea categoriei:', error);
      message.error('Eroare la actualizarea categoriei');
      return false;
    }
  }, [token, fetchCategories]);

  const deleteCategory = useCallback(async (id) => {
    if (!token) return false;
    
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/product_category/${id}`, {
        params: { token }
      });
      
      message.success('Categoria a fost ștearsă cu succes');
      fetchCategories();
      return true;
    } catch (error) {
      console.error('Eroare la ștergerea categoriei:', error);
      message.error('Eroare la ștergerea categoriei');
      return false;
    }
  }, [token, fetchCategories]);

  // Funcție helper pentru a obține toate categoriile într-o listă plată pentru Select
  const getFlatCategories = useCallback(() => {
    const flatList = [];
    
    const addToFlat = (categories, level = 0) => {
      categories.forEach(category => {
        flatList.push({
          ...category,
          level,
          displayName: '  '.repeat(level) + category.category_name
        });
        if (category.children && category.children.length > 0) {
          addToFlat(category.children, level + 1);
        }
      });
    };
    
    addToFlat(categoriesTree);
    return flatList;
  }, [categoriesTree]);

  // Adăugăm o funcție pentru refresh explicit
  const refreshCategories = useCallback(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  return {
    categories,
    categoriesTree,
    loading,
    fetchCategories,
    refreshCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    getFlatCategories
  };
}; 